import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { useAuth } from '../store';
import { changeUsername, changePassword, logOut } from '../services/auth';
import { isUltraAdmin } from '../utils';

/**
 * User Account Page
 * Shows user account information and allows updating username/password
 */
export function UserAccountPage() {
  const navigate = useNavigate();
  const { currentUser, isLoading, refreshUser } = useAuth();
  
  const [editingUsername, setEditingUsername] = useState(false);
  const [editingPassword, setEditingPassword] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    navigate('/login');
    return null;
  }

  // Debug ultra-admin check
  console.log('Current user email:', currentUser.email);
  console.log('Is ultra-admin:', isUltraAdmin(currentUser));

  const handleUsernameSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!newUsername.trim()) {
      setError('Username cannot be empty');
      setLoading(false);
      return;
    }

    if (newUsername.trim().length > 20) {
      setError('Username must be 20 characters or less');
      setLoading(false);
      return;
    }

    try {
      await changeUsername(newUsername.trim());
      await refreshUser();
      setSuccess('Username updated successfully!');
      setEditingUsername(false);
      setNewUsername('');
    } catch (err: any) {
      setError(err.message || 'Failed to update username');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!newPassword || !confirmPassword) {
      setError('Please fill in all password fields');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await changePassword(newPassword);
      setSuccess('Password updated successfully!');
      setEditingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: any) {
      setError(err.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (err: any) {
      setError(err.message || 'Failed to sign out');
    }
  };

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">Account</h1>
            <p className="text-sm text-secondary-text">{currentUser.username}</p>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/games')}
          >
            Back to Games
          </Button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Status Messages */}
        {error && (
          <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Account Info */}
        <Card padding="md">
          <h2 className="text-lg font-semibold text-primary-text mb-4">Account Information</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm text-secondary-text">Username</div>
                {!editingUsername && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setEditingUsername(true);
                      setNewUsername(currentUser.username);
                      setError('');
                      setSuccess('');
                    }}
                  >
                    Edit
                  </Button>
                )}
              </div>
              
              {editingUsername ? (
                <form onSubmit={handleUsernameSubmit} className="space-y-2">
                  <input
                    type="text"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    className="w-full px-3 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="New username"
                    maxLength={20}
                    disabled={loading}
                  />
                  <div className="flex gap-2">
                    <Button type="submit" variant="primary" size="sm" disabled={loading}>
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setEditingUsername(false);
                        setNewUsername('');
                        setError('');
                      }}
                      disabled={loading}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="text-primary-text font-medium">{currentUser.username}</div>
              )}
            </div>

            <div>
              <div className="text-sm text-secondary-text mb-1">Email</div>
              <div className="text-primary-text font-medium">{currentUser.email || 'Not provided'}</div>
            </div>

            {currentUser.createdAt && (
              <div>
                <div className="text-sm text-secondary-text mb-1">Member Since</div>
                <div className="text-primary-text font-medium">
                  {new Date(currentUser.createdAt).toLocaleDateString()}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Change Password */}
        <Card padding="md">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-primary-text">Password</h2>
            {!editingPassword && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setEditingPassword(true);
                  setError('');
                  setSuccess('');
                }}
              >
                Change Password
              </Button>
            )}
          </div>

          {editingPassword ? (
            <form onSubmit={handlePasswordSubmit} className="space-y-3">
              <div>
                <label className="block text-sm text-secondary-text mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="At least 6 characters"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="block text-sm text-secondary-text mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-3 py-2 bg-secondary-bg border border-border rounded text-primary-text focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Re-enter password"
                  disabled={loading}
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={loading}>
                  Update Password
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setEditingPassword(false);
                    setNewPassword('');
                    setConfirmPassword('');
                    setError('');
                  }}
                  disabled={loading}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <p className="text-sm text-secondary-text">••••••••</p>
          )}
        </Card>

        {/* Ultra Admin Section */}
        {isUltraAdmin(currentUser) && (
          <Card padding="md">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-semibold text-primary-text">Ultra Admin</h2>
                <Badge variant="error" size="sm">ADMIN</Badge>
              </div>
              <p className="text-sm text-secondary-text">
                You have special administrator privileges.
              </p>
              <div className="space-y-2">
                <Button 
                  variant="primary" 
                  fullWidth
                  onClick={() => navigate('/ultra-admin/templates')}
                >
                  📝 Manage Templates
                </Button>
                <Button 
                  variant="primary" 
                  fullWidth
                  onClick={() => navigate('/ultra-admin/user-events')}
                >
                  🎮 All User Events
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Sign Out */}
        <Card padding="md">
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-primary-text">Sign Out</h2>
            <p className="text-sm text-secondary-text">
              Sign out of your account on this device.
            </p>
            <Button variant="secondary" onClick={handleSignOut}>
              Sign Out
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
